<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <h4 class="mb-0" v-if="parameters.remitFromOrder">Remitos desde Pedidos</h4>
          <ModuleViewHeading
            v-if="parameters.remitFromSale"
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Agregar Remito"
                        aria-label="Agregar Remito"
                        @click="add()">
                <AppIcon name="add" label="Agregar Remito" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;">
              <label class="filter-label">Punto de Venta</label>
              <v-select :options="arr.filters.points_sales" v-model="filters.points_sales" placeholder="Todos" :multiple="false" :select-on-tab="true"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Clientes</label>
              <v-select :options="arr.filters.customers" v-model="filters.customers" placeholder="Todos" :multiple="true" :select-on-tab="true"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;" v-if="parameters.remitFromOrder">
              <label class="filter-label">Facturado</label>
              <v-select :options="arr.filters.invoiced" v-model="filters.invoiced" placeholder="Todos" :multiple="false" :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterRemits()" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12">
          <b-card>
            <b-row>
              <b-col sm="12">
                <b-table class="app-table"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        :tbody-tr-class="rowClass"
                        @row-clicked="onRowClicked"
                        :busy="table.isBusy"
                        v-if="table.items.length || table.isBusy">

                    <template v-slot:table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"
                        :key="field.key"
                        :style="{ width: field.width }"
                      >
                    </template>

                    <template v-slot:table-busy>
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Cargando...</strong>
                      </div>
                    </template>

                    <template v-slot:cell(business)="data">
                      {{data.item.point_sale.business.reference}}
                    </template>

                    <template v-slot:cell(id)="data">
                      <div class="text-nowrap" v-if="data.item.type_voucher && data.item.point_sale">
                        {{data.item.type_voucher.name}} {{data.item.point_sale.point_sale.toString().padStart(4,'0') + '-' + data.item.number.toString().padStart(8,'0')}}
                      </div>
                    </template>

                    <template v-slot:cell(date)="data">
                      {{moment(data.item.date).format('DD/MM/YYYY')}}
                    </template>

                    <template v-slot:cell(staff_id)="data">
                      <span v-if="data.item.staff_id" v-b-tooltip.hover :title="'STAFF: ' + data.item.staff.name">
                        <b-avatar :src="data.item.staff.image"
                                  v-if="data.item.staff.image">
                        </b-avatar>
                        <b-avatar v-else
                                  :text="avatarFromName(data.item.staff.name).text"
                                  :style="avatarFromName(data.item.staff.name).style">
                        </b-avatar>
                        <span v-if="isMobileViewport" class="ml-2">{{data.item.staff.name}}</span>
                      </span>
                    </template>

                    <template v-slot:cell(customers_id)="data">
                      <div v-if="data.item.customer">
                        <b-avatar :src="data.item.customer.image"
                                  v-if="data.item.customer.image">
                        </b-avatar>
                        <b-avatar v-else
                                  :text="avatarFromName(data.item.customer.name).text"
                                  :style="avatarFromName(data.item.customer.name).style">
                        </b-avatar>
                        &nbsp;<b>{{data.item.customer.name}}</b>

                        <AppIcon name="circle-fill" v-if="!data.item.customer.active" class="ml-2 text-danger" v-b-tooltip.hover title="Cliente INACTIVO" />
                      </div>
                    </template>

                    <template v-slot:cell(observations)="data">
                      <div v-if="data.item.observations">
                        <span v-if="isMobileViewport">{{data.item.observations}}</span>
                        <span v-else v-b-tooltip.hover :title="data.item.observations">
                          <AppIcon name="chat-square-text" />
                        </span>
                      </div>
                    </template>

                    <template v-slot:cell(f_action)="data">
                      <b-dropdown right
                                  no-caret
                                  size="sm"
                                  variant="link"
                                  boundary="window"
                                  toggle-class="app-table-action-toggle"
                                  class="pull-right">
                        <template #button-content>
                          <AppIcon name="more_vert" label="Acciones" />
                        </template>

                        <b-dropdown-item @click="sendPrinter(data.item)">
                          <AppIcon name="print" /> Imprimir
                        </b-dropdown-item>
                        <b-dropdown-item @click="generateInvoice(data.item)" v-if="canInvoice && !data.item.sale_id && parameters.remitFromOrder">
                          <AppIcon name="receipt_long" /> Facturar
                        </b-dropdown-item>

                        <b-dropdown-header v-if="access.elements.deleteRemits">Acciones</b-dropdown-header>
                        <b-dropdown-item @click="remove(data.item)" v-if="access.elements.deleteRemits">
                          <AppIcon name="delete" class="text-danger" /> Eliminar
                        </b-dropdown-item>
                      </b-dropdown>
                    </template>
                </b-table>
                <EmptyState v-else
                            icon="reports"
                            title="No se encontraron remitos"
                            description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda, o generá un remito nuevo con el botón Agregar." />

                <div class="app-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">
                    {{ table.tablePaginate.total }} registros
                  </span>
                  <export-excel
                      class="list-view-link"
                      :data="arr.export"
                      worksheet="Listado de Remitos"
                      name="list-remits.xls">
                      <AppIcon name="download" class="mr-1" /> Exportar
                  </export-excel>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- DRAWER: detalle del remito seleccionado -->
      <b-sidebar
          id="sidebar-remit-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle del Remito"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                {{itemSelected.type_voucher.name}} {{itemSelected.point_sale.point_sale.toString().padStart(4,'0') + '-' + itemSelected.number.toString().padStart(8,'0')}}
              </span>
              <div v-if="itemSelected.customer">{{itemSelected.customer.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevRemit"
                        v-b-tooltip.hover
                        title="Remito anterior"
                        @click="goPrevRemit()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextRemit"
                        v-b-tooltip.hover
                        title="Remito siguiente"
                        @click="goNextRemit()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="list-ul" /> Detalle
            </div>

            <b-table  class="app-table app-table--compact"
                      responsive
                      stacked
                      :hover="true"
                      :small="true"
                      :fixed="false"
                      :items="tableSub.items"
                      :fields="tableSub.fields"
                      v-if="tableSub.items.length">

              <template v-slot:cell(quantity)="row">
                {{row.item.quantity}}
                <span v-if="row.item.unit_measurement">{{row.item.unit_measurement.reference}}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="getProductNamePlain(row.item)" v-html="getProductName(row.item)"></div>
              </template>

            </b-table>
            <EmptyState v-else icon="list-ul" title="Sin items cargados" />
          </div>

          <b-button v-if="tableSub.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <!-- En mobile no hay kebab en la fila (ver loadFieldTable): las
               mismas acciones quedan acá, ya desplegadas, sin menú. En
               desktop el kebab de la fila sigue existiendo, pero acceder a
               estas acciones también desde el propio drawer evita volver a
               la tabla — quedan visibles en los dos anchos. -->
          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinter(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" v-if="canInvoice && !itemSelected.sale_id && parameters.remitFromOrder" @click="generateInvoice(itemSelected)">
              <AppIcon name="receipt_long" class="mr-1" /> Facturar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && access.elements.deleteRemits">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterRemits()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="crud.print.id"
              :reference="crud.print.reference"
              :registerID="crud.print.id"
              :key="crud.print.key"
              :manyFormat="true"/>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close
              size="xl"
              :key="modal.form._id"
              :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
        </div>

        <form-wizard  title=""
                      subtitle=""
                      nextButtonText="Siguiente"
                      backButtonText="Atrás"
                      finishButtonText="Guardar"
                      color="#2f353a"
                      shape="tab"
                      :start-index="wizard.startIndex"
                      @on-complete="save"
                      class="crud-remits-wizard">

          <tab-content title="General" icon="fa fa-edit" :before-change="saleValidDetailGeneral">
            <b-row>
              <b-col md="4">
                <b-form-group label="Fecha">
                  <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="5">
                <FindObject render="search"
                            type="customers"
                            @select-object="loadCustomers($event)"
                            :valueID="crud.form.customers_id"
                            :where="conditionCustomersActive"
                            :key="crud.form.customers_id"
                            :disabled="crud.form.id>0 || remitFrom" />
              </b-col>
              <b-col md="3">
                <FindObject render="search"
                            type="erpPointsSales"
                            auto-select-single
                            display="full_identification"
                            @select-object="loadPointsSales($event)"
                            :valueID="crud.form.points_sales_id"
                            :key="crud.form.points_sales_id"
                            :disabled="crud.form.id>0 || remitFrom"
                            :where="conditionPointsSalesActive"  />
              </b-col>
            </b-row>
          </tab-content>

          <tab-content title="Items" icon="fa fa-list" :before-change="saleValidDetailItems">
            <b-row class="mb-3" v-if="!parameters.remitMultipleItems">
              <b-col lg="6" md="6" sm="6" v-if="crud.formVouchersPending.length">

                <div v-if="parameters.remitFromOrder">
                  <b-form-group label="Pedidos">
                    <b-form-select v-model="crud.form.reference_voucher_id" :options="crud.formVouchersPending" size="sm" @input="filtersItems" :disabled="remitFrom"></b-form-select>
                  </b-form-group>
                </div>
                <div v-if="parameters.remitFromSale">
                  <b-form-group label="Comprobante de Venta">
                    <b-form-select v-model="crud.form.reference_voucher_id" :options="crud.formVouchersPending" size="sm" @input="filtersItems"></b-form-select>
                  </b-form-group>
                </div>

              </b-col>
            </b-row>
            <b-row>
              <b-col lg="12" md="12" sm="12">
                <b-overlay :show="overlay.remits" rounded="sm">
                  <b-table-simple hover small caption-top responsive v-if="arr.pending.length" class="app-table app-table--compact app-table--stacked-manual crud-remit-table-items">
                    <b-thead>
                      <b-tr>
                        <b-th width="5%" class="text-left">Remitir</b-th>
                        <b-th width="20%" class="text-left">Comprobante</b-th>
                        <b-th width="10%" class="text-left">Código</b-th>
                        <b-th width="30%" class="text-left">Descripción</b-th>
                        <b-th width="5%" class="text-center">Est.Vto.</b-th>
                        <b-th width="10%" class="text-center">Cantidad</b-th>
                        <b-th width="10%" class="text-center">Remitido</b-th>
                        <b-th width="10%" class="text-center">A Remitir</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody v-for="(item, index) in arr.pending" :key="item.id">
                      <b-tr :key="itemForceUpdate" v-if="!item.services_id && !item.concepts_id && crud.formPending[index]" :variant="crud.formPending[index].vto_status_alert">
                        <b-td data-label="Remitir" :class="'align-middle ' + alignClass('text-left')">
                          <b-form-checkbox v-model="crud.formPending[index].check"
                                            @change="validCheck(index)"
                                            v-if="!(crud.formPending[index].vto_status == 'VENCIDO')"
                                            switch
                                            size="sm"
                                            class="pull-left">
                          </b-form-checkbox>
                        </b-td>
                        <b-td data-label="Comprobante" :class="'align-middle ' + alignClass('text-left')">
                          {{crud.formPending[index].voucher}}
                        </b-td>
                        <b-td data-label="Código" :class="'align-middle ' + alignClass('text-left')">
                          <div v-html="getProductCode(item)"></div>
                        </b-td>
                        <b-td data-label="Descripción" :class="'align-middle ' + alignClass('text-left')">
                          <div v-html="getProductName(item)"></div>
                        </b-td>
                        <b-td data-label="Est. Vto." :class="'align-middle ' + alignClass('text-center')">
                          <div v-if="crud.formPending[index].vto">
                            {{crud.formPending[index].vto_status}}
                          </div>
                        </b-td>
                        <b-td data-label="Cantidad" :class="'align-middle ' + alignClass('text-center')">
                          {{item.quantity}}
                          <span v-if="item.unit_measurement">{{item.unit_measurement.reference}}</span>
                        </b-td>
                        <b-td data-label="Remitido" :class="'align-middle ' + alignClass('text-center')">
                          {{item.quantity_remit}}
                          <span v-if="item.unit_measurement">{{item.unit_measurement.reference}}</span>
                        </b-td>
                        <b-td data-label="A remitir" :class="'align-middle ' + alignClass('text-center')">
                          <app-text-input type="number"
                                          size="sm"
                                          step="0.01"
                                          min="0"
                                          @change="validQuantity(index)"
                                          v-model="crud.formPending[index].quantity"
                                          placeholder="Ingresar cantidad"
                                          class="crud-remit-item-input"
                                          v-if="crud.formPending[index].check" />
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                  <EmptyState v-else-if="crud.formVouchersPending.length" icon="list-ul" title="Seleccioná un comprobante" />
                  <EmptyState v-else icon="list-ul" title="No hay productos para remitir" />
                </b-overlay>
              </b-col>
            </b-row>
          </tab-content>

          <tab-content title="Observaciones" icon="fa fa-flag-checkered">
            <b-row>
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea
                    v-model="crud.form.observations"
                    placeholder="Ingresar las observaciones..."
                    rows="3"
                    max-rows="6"
                    lazy
                  ></app-textarea>
                </b-form-group>
              </b-col>
            </b-row>
          </tab-content>

        </form-wizard>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="cancel()">Cancelar</b-button>
        </div>
      </b-modal>

      <!-- CRUD DETAILS -->
      <b-modal v-model="modal.detail.active"
              size="xl"
              dialog-class="remit-detail-modal-wide"
              :key="modal.detail._id"
              :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{this.modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title><AppIcon name="description" class="mr-1" /> General</template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b-badge variant="light" class="remit-detail-id">#{{this.itemSelected.id}}</b-badge></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.point_sale && itemSelected.point_sale.business">
                    <span class="app-modal-detail-list__label">Empresa</span>
                    <span class="app-modal-detail-list__value"><b-badge variant="primary" class="remit-detail-business">{{this.itemSelected.point_sale.business.reference}}</b-badge></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value remit-detail-voucher"><AppIcon name="receipt" class="mr-1" /><b>{{this.itemSelected.type_voucher.name + ' '  + this.itemSelected.point_sale.point_sale.toString().padStart(4,'0') + '-' + this.itemSelected.number.toString().padStart(8,'0')}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.sale && itemSelected.sale.type_voucher && itemSelected.sale.point_sale">
                    <span class="app-modal-detail-list__label">Factura Vinculada</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.sale.type_voucher.name + ' '  + this.itemSelected.sale.point_sale.point_sale.toString().padStart(4,'0') + '-' + this.itemSelected.sale.number.toString().padStart(8,'0')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff || itemSelected.seller">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value remit-detail-staff" v-if="itemSelected.staff">
                      <b-avatar :src="itemSelected.staff.image" v-if="itemSelected.staff.image" size="sm" />
                      <b-avatar v-else size="sm" :text="avatarFromName(itemSelected.staff.name).text" :style="avatarFromName(itemSelected.staff.name).style" />
                      <span>{{itemSelected.staff.name}}</span>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(this.itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Punto de Venta</span>
                    <span class="app-modal-detail-list__value"><b-badge variant="info" class="remit-detail-point-sale"><AppIcon name="shop" class="mr-1" />{{this.itemSelected.point_sale.point_sale}} · {{this.itemSelected.point_sale.name}}</b-badge></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.customer">
                    <span class="app-modal-detail-list__label">Cliente</span>
                    <span class="app-modal-detail-list__value remit-detail-staff">
                      <b-avatar :src="itemSelected.customer.image" v-if="itemSelected.customer.image" size="sm" />
                      <b-avatar v-else size="sm" :text="avatarFromName(itemSelected.customer.name).text" :style="avatarFromName(itemSelected.customer.name).style" />
                      <span>{{this.itemSelected.customer.name}}</span>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.reference_origin">
                    <span class="app-modal-detail-list__label">Origen</span>
                    <span class="app-modal-detail-list__value" v-html="itemSelected.reference_origin"></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.observations}}</span>
                  </div>
                </div>
              </b-tab>
              <b-tab>
                <template #title><AppIcon name="list-ul" class="mr-1" /> Items</template>
                <b-table class="mb-0 app-table app-table--compact"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetail.items"
                        :fields="tableDetail.fields"
                        v-if="tableDetail.items.length">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>

                  <template v-slot:cell(code)="data">
                    <div v-html="getProductCode(data.item)"></div>
                  </template>

                  <template v-slot:cell(product)="data">
                    <div class="text-truncate" v-b-tooltip.hover :title="getProductNamePlain(data.item)" v-html="getProductName(data.item)"></div>
                  </template>

                  <template v-slot:cell(quantity)="data">
                    {{data.item.quantity}}
                    <span v-if="data.item.unit_measurement">{{data.item.unit_measurement.reference}}</span>
                  </template>

                  <template v-slot:cell(stock_control)="data">
                    <b-badge :variant="stockBadgeVariant(data.item)" :title="stockBadgeTitle(data.item)">
                      {{ stockBadgeLabel(data.item) }}
                    </b-badge>
                  </template>

                </b-table>
                <EmptyState v-else icon="list-ul" title="No se encontraron registros" />
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false">Cancelar</b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Param from '@/config/parameters'
  import FindObject from '@/components/inc/find/findObject'
  import Printer from '@/components/inc/printer/printer'
  import moment from 'moment'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'
  import Funciones from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      FindObject,
      Printer,
      FormWizard,
      TabContent,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.REMITOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'remits',
          elements: {
            deleteRemits: true
          }
        },
        table : {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,
        },
        tableSub : {
          items: [],
          fields: []
        },
        tableDetail : {
          items: [],
          fields: []
        },
        itemSelected: null,
        detailDrawer: false,
        filtersOpen: false,
        // Reactivo al viewport real (matchMedia), nunca un string fijo:
        // BootstrapVue apaga sticky-header si `stacked` está seteado a
        // cualquier valor truthy, incluso en desktop.
        isMobileViewport: false,
        crud: {
          form: {
            id: 0,
            date: '',
            date_expiration: '',
            types_vouchers_id: 0,
            types_vouchers: null,
            letter: '',
            number: 0,
            points_sales_id: 0,
            points_sales: null,
            observations: '',
            staff_id: 0,
            customers_id: 0,
            customers: null,
            detail: [],
            selectTypeVoucher: 'remito',
            reference_voucher_id: 0,
            orders: null,
            orders_id: 0,
          },
          formVouchersPending: [],
          formPending: [],
          print: {
            reference: '',
            id: 0,
            key: 0,
          }
        },
        modal: {
          form: {
            active: false,
            title: '',
          },
          detail: {
            active: false,
            title: '',
          },
        },
        arr: {
          remits: [],
          pending: [],
          export: [],
          filters : {
            customers: [],
            points_sales: [],
            invoiced: [
              {code: true, label: 'Facturado'},
              {code: false, label: 'No Facturado'}
            ]
          },
        },
        filters: {
          date_start: '',
          date_end: '',
          customers: null,
          points_sales: null,
          invoiced: null
        },
        itemForceUpdate: 0,
        parameters: {
          remitFromOrder: Helper.hasParametersAccess(Param.P26),
          remitFromSale: Helper.hasParametersAccess(Param.P27),
          remitMultipleItems: Helper.hasParametersAccess(Param.P28),
        },
        wizard: {
          startIndex: 0,
        },
        overlay: {
          remits: false,
        }
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      this.access.elements.deleteRemits = Helper.hasAccessElement(this.access, 'delete_remits')
      /* Fin configuracion */
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_remits') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldTable()
      this.filterLoad()
      this.filterRemits()

      this.crud.form.orders_id = this.$route.params.ordersID
      if(this.crud.form.orders_id) {
        this.remitsFromOrders()
        // Vuelve a la ruta simple apenas se consume el param -- si no, la
        // pestaña queda con el pedido pegado en la URL para siempre y
        // volver a ella (activated, keep-alive) generaría el remito de
        // nuevo aunque el usuario solo quiera ver el listado.
        this.$router.replace({ name: 'RemitsStaffRemits' }).catch(() => {})
      }

      // Acción directa desde la paleta de comandos: abrir el alta al entrar.
      if (this.$route.query.new == '1') {
        this.add()
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterRemits()

      // Mismos chequeos que en mounted(): si la pestaña ya estaba montada
      // (keep-alive, mismo fullPath que una invocación anterior, ej.
      // "Remitir" dos veces seguidas sobre el mismo pedido), "mounted" no
      // vuelve a correr y ninguna de estas acciones abriría el formulario
      // una segunda vez sin repetirlas acá.
      this.crud.form.orders_id = this.$route.params.ordersID
      if(this.crud.form.orders_id) {
        this.remitsFromOrders()
        this.$router.replace({ name: 'RemitsStaffRemits' }).catch(() => {})
      }

      if (this.$route.query.new == '1') {
        this.add()
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    computed: {
      canInvoice() {
        return Helper.canInvoice()
      },
      // NAVEGACION DEL DRAWER (anterior/siguiente sobre las filas cargadas)
      currentRemitIndex() {
        if(!this.itemSelected || !this.table.items) {
          return -1
        }
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevRemit() {
        return this.currentRemitIndex > 0
      },
      canGoNextRemit() {
        if(this.currentRemitIndex < 0) {
          return false
        }
        return this.currentRemitIndex < (this.table.items.length - 1)
      },

      // CONDITIONS SELECT
      conditionCustomersActive(){
        return [{field: 'active', condition: true}];
      },
      conditionPointsSalesActive(){
        return [{field: 'active', condition: true}];
      },

      // REMIT DESDE
      remitFrom() {
        var status = false
        if(this.crud.form.orders_id) {
          status = true
        }
        return status
      }
    },
    watch: {
      filtersOpen: function (value) {
        StorageLocal.setValue('filters_open_crud_remits', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldTable()
      },
      'crud.form.customers_id': function () {
        this.arr.pending = []
        this.crud.form.reference_voucher_id = 0
        this.searchVoucherByCustomer()
      },
      'crud.form.points_sales_id': function () {
        this.arr.pending = []
        this.crud.form.reference_voucher_id = 0
        this.searchVoucherByCustomer()
      },
    },
    methods: {
      // RESPONSIVE
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Funciones.avatarFromName(name)
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      // Click en la fila abre el drawer de detalle, salvo que el click haya
      // sido sobre un control (el dropdown de acciones, un link, etc.).
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      // Resalta en la tabla la fila del registro que está abierto en el
      // drawer, para no perder la referencia visual mientras se navega con
      // los botones anterior/siguiente.
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },

      // CONFIGURACION
      loadFieldTable () {
        // TABLE COMPROBANTES
        this.table.fields = []
        this.table.fields.push({key: 'business', label: 'Empresa', class:"align-middle " + this.alignClass('text-left'), width:"60px"})
        this.table.fields.push({key: 'id', label: 'Comprobante', class:"align-middle " + this.alignClass('text-center'), width:"140px"})
        this.table.fields.push({key: 'date', label: 'Fecha', class:"align-middle " + this.alignClass('text-center'), width:"80px"})
        this.table.fields.push({key: 'customers_id', label: 'Cliente', class:"align-middle", width:"220px"})
        this.table.fields.push({key: 'staff_id', label: 'Staff', class:"align-middle " + this.alignClass('text-center'), width:"60px"})
        // En mobile va como texto plano (no ícono+tooltip), así que necesita
        // un label real; en desktop es una columna angosta solo-ícono.
        this.table.fields.push({key: 'observations', label: this.isMobileViewport ? 'Observaciones' : '', class:"align-middle " + this.alignClass('text-center'), width:"25px"})
        // El kebab de acciones no va en mobile: el drawer ya tiene las
        // mismas acciones desplegadas (Imprimir/Facturar/Eliminar), visibles
        // ahí en cualquier ancho.
        if(!this.isMobileViewport) {
          this.table.fields.push({key: 'f_action', label:'', class:"align-middle", width:"60px"})
        }

        // TABLE DETALLE (drawer, siempre vertical: alineación fija a la
        // izquierda, el b-sidebar tiene un ancho fijo que no depende de
        // isMobileViewport)
        this.tableSub.fields = []
        this.tableSub.fields.push({key: 'quantity', label: 'Cantidad', class:"align-middle text-left", width:"30%"})
        this.tableSub.fields.push({key: 'product', label: 'Producto', class:"align-middle text-left", width:"70%"})

        // TABLE DETALLE COMPLETO (modal, responsive)
        this.tableDetail.fields = []
        this.tableDetail.fields.push({key: 'code', label: 'Código', class:"align-middle " + this.alignClass('text-left'), width:"90px"})
        this.tableDetail.fields.push({key: 'product', label: 'Producto', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"260px"})
        this.tableDetail.fields.push({key: 'stock_control', label: 'Stock', class:"align-middle " + this.alignClass('text-center'), width:"125px"})
        this.tableDetail.fields.push({key: 'quantity', label: 'Cantidad', class:"align-middle " + this.alignClass('text-right'), width:"100px"})
      },
      getRowCount(items) {
        return items.length
      },

      // SELECT
      loadCustomers(object) {
        if(object){
          this.crud.form.customers = object
          this.crud.form.customers_id = object.id

          if(object.data_fiscal) {
            if(object.data_fiscal.points_sales_id) {
              if(!this.crud.form.points_sales_id) {
                this.crud.form.points_sales = object.data_fiscal.points_sales
                this.crud.form.points_sales_id = object.data_fiscal.points_sales_id
              }
            }
          }
        } else {
          this.crud.form.customers = null
          this.crud.form.customers_id = 0
        }
      },
      loadPointsSales(object) {
        if(object){
          this.crud.form.points_sales = object
          this.crud.form.points_sales_id = object.id
        } else {
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0
        }
      },
      prepareExport(data) {
        this.arr.export = []
        data.forEach(element => {
            var businessID = 0
            var businessName = ""
            var customersID = 0
            var customersCode = ""
            var customersName = ""
            var voucherReference = ""

            if(element.point_sale && element.point_sale.business) {
              businessID = element.point_sale.business.id
              businessName = element.point_sale.business.reference
            }
            if(element.customer) {
              customersID = element.customer.id
              customersCode = element.customer.code
              customersName = element.customer.name
            }
            if(element.type_voucher) {
              voucherReference = element.type_voucher.name + ' ' + element.point_sale.point_sale.toString().padStart(4,'0') + '-' + element.number.toString().padStart(8,'0')
            }

            this.arr.export.push({
              id: element.id,
              businessID: businessID,
              businessName: businessName,
              date: element.date,
              voucherReference: voucherReference,
              customersID: customersID,
              customersCode: customersCode,
              customersName: customersName,
              observations: element.observations,
              productID: 0,
              productCode: '',
              productName: '',
              productCompoundID: 0,
              productCompoundName: "",
              quantity: 0,
              unitMeasurementID: 0,
              unitMeasurementName: "",
            })

            element.details.forEach(element1 => {
              var productID = 0
              var productCode = ""
              var productName = ""
              var productCompoundID = 0
              var productCompoundName = ""
              var unitMeasurementID = 0
              var unitMeasurementName = ""

              var item = null
              if(element1.order_detail_id) {
                item = element1.orders_detail
              }
              if(element1.sale_detail_id) {
                item = element1.sales_detail
              }

              if(item.products_id) {
                productID = item.product.id
                productCode = item.product.code
                productName = item.product.name
              }

              if(item.products_colors_id) {
                productID = item.products_color.id
                productCode = item.products_color.code
                productName = item.products_color.name + " (" + item.products_color.color.name + ")"
              }

              if(item.products_waist_id) {
                productID = item.products_waist.id
                productCode = item.products_waist.code
                productName = item.products_waist.name + " (" + item.products_waist.waist.name + ")"
              }

              if(item.products_color_waist_id) {
                productID = item.products_color_waist.id
                productCode = item.products_color_waist.code
                productName = item.products_color_waist.name + " (" + item.products_color_waist.color.name + " - " + item.products_color_waist.waist.name + ")"
              }

              if(item.products_compound_id) {
                productCompoundID = item.products_compound.id
                productCompoundName = item.products_compound.name
              }

              if(element1.unit_measurement_id) {
                unitMeasurementID = element1.unit_measurement.id
                unitMeasurementName = element1.unit_measurement.name
              }

              this.arr.export.push({
                productID: productID,
                productCode: productCode,
                productName: productName,
                productCompoundID: productCompoundID,
                productCompoundName: productCompoundName,
                quantity: element1.quantity.replace('.',','),
                unitMeasurementID: unitMeasurementID,
                unitMeasurementName: unitMeasurementName,
              })
            })
          });
      },

      // ABM MAIN
      add() {
        this.crud.form.id = 0
        this.crud.form.date = moment().format('YYYY-MM-DD')
        this.crud.form.observations = ''
        this.crud.form.staff_id = Helper.getStaffId()
        this.crud.form.customers_id = 0
        this.crud.form.customers = null
        this.crud.form.points_sales_id = 0
        this.crud.form.points_sales = null
        this.crud.form.detail = []

        this.crud.formPending = []
        this.arr.pending = []
        this.crud.form.reference_voucher_id = 0

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Remito"
        this.modal.form.active = true
      },
      remove(item) {
        this.crud.form.id = item.id

        var voucher = "ID #" + item.id
        if(item.type_voucher && item.point_sale) {
          voucher = item.type_voucher.name + ' ' + item.point_sale.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
        }

        this.confirmDialog('¿Desea borrar el remito ' + voucher + '?', {
          title: 'Borrar Remito',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarRemito(this.crud.form.id);

            result.then(() => {
              this.modal.form.active = false
              if(this.itemSelected && this.itemSelected.id === this.crud.form.id) {
                this.hideSub()
              }
              loader.hide()
              this.filterRemits()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      save() {
        this.confirmDialog('¿Desea continuar con la generación del remito?', {
          title: 'Generar Remito',
          okTitle: 'GENERAR'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();

            var result = serviceAPI.agregarRemito(this.crud.form);

            result.then(() => {
              this.modal.form.active = false

              // blanqueo remit from
              this.crud.form.orders_id = 0
              this.crud.form.orders = null
              this.wizard.startIndex = 0

              loader.hide()
              this.filterRemits()
              this.$awn.success("Remito generado con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },

      // DETALLE REMITO
      saleValidDetailGeneral() {
        if(this.crud.form.customers_id && this.crud.form.points_sales_id) {

          if(this.parameters.remitMultipleItems) {
            this.obtenerPendienteRemitirByCliente(this.crud.form.customers_id,0,this.crud.form.points_sales_id)
          } else {
            this.obtenerVouchersByCliente(this.crud.form.customers_id,this.crud.form.points_sales_id)
          }

          return true;
        } else {
          if(!this.crud.form.customers_id) {
            this.$awn.alert("No se cargó el cliente");
            return false;
          }
          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta");
            return false;
          }
        }
      },
      obtenerVouchersByCliente(customer_id=0, points_sales_id=0) {
        this.overlay.remits = true
        this.crud.formVouchersPending = []

        var result = serviceAPI.filtrarVoucherPendienteRemitir({'customers_id':customer_id, 'points_sales_id':points_sales_id})
        result.then((response) => {
          var data = response.data
          this.overlay.remits = data.length > 0

          if(data) {
            data.forEach(element => {
              if(this.parameters.remitFromOrder) {
                this.crud.formVouchersPending.push({
                  value: element.id,
                  text: 'Pedido Nº: ' + element.id + ' | ' + moment(element.date).format('DD/MM/YYYY'),
                })
              }

              if(this.parameters.remitFromSale) {
                this.crud.formVouchersPending.push({
                  value: element.id,
                  text: element.type_voucher.name + ' ' + element.point_sale.point_sale.toString().padStart(4,'0') + '-' + element.number.toString().padStart(8,'0') + ' | ' + moment(element.date).format('DD/MM/YYYY'),
                })
              }
            });
          }

          this.overlay.remits = false
        })
      },
      filtersItems(vouchers_id) {
        this.obtenerPendienteRemitirByCliente(this.crud.form.customers_id, vouchers_id, this.crud.form.points_sales_id)
      },
      obtenerPendienteRemitirByCliente(customer_id=0, vouchers_id=0, points_sales_id=0) {
        this.overlay.remits = true
        this.crud.formPending = []

        var result = serviceAPI.filtrarPendienteRemitir({'customers_id':customer_id, 'vouchers_id':vouchers_id, 'points_sales_id': points_sales_id})
        result.then((response) => {
          var data = response.data
          this.overlay.remits = data.length > 0

          this.arr.pending = data
          this.arr.pending.forEach(element => {
            if(this.parameters.remitFromOrder) {
              this.crud.formPending.push({
                check: false,
                voucher: 'ORDEN DE PEDIDO ' + element.order.points_sales.point_sale.toString().padStart(4,'0') + '-' + element.order.id.toString().padStart(8,'0'),
                quantity: parseFloat(element.quantity) - parseFloat(element.quantity_remit),
                maxQuantity: parseFloat(element.quantity) - parseFloat(element.quantity_remit),
                order_detail_id: element.id,
                description: element.referenceProduct,
                vto: 0,
                vto_status: 'LIBRE',
                vto_status_alert: '',
                unit_measurement_id: element.unit_measurement_id,
              })
            }

            if(this.parameters.remitFromSale) {
              this.crud.formPending.push({
                check: false,
                voucher: element.sale.type_voucher.name + ' ' + element.sale.point_sale.point_sale.toString().padStart(4,'0') + '-' + element.sale.number.toString().padStart(8,'0'),
                quantity: parseFloat(element.quantity) - parseFloat(element.quantity_remit),
                maxQuantity: parseFloat(element.quantity) - parseFloat(element.quantity_remit),
                sale_detail_id: element.id,
                description: element.description,
                vto: element.vto.days,
                vto_status: element.vto.status,
                vto_status_alert: element.vto.alert,
                unit_measurement_id: element.unit_measurement_id,
              })
            }
          });

          this.overlay.remits = false
        })
      },
      validCheck(index) {
        if(!this.crud.formPending[index].check) {
          this.crud.formPending[index].quantity = this.crud.formPending[index].maxQuantity
          this.forceUpdate()
        }
      },
      validQuantity(index){
        if(this.crud.formPending[index].quantity=='') {
          this.crud.formPending[index].quantity = 0
          this.crud.formPending[index].check = false
        }

        if( parseFloat(this.crud.formPending[index].quantity) > parseFloat(this.crud.formPending[index].maxQuantity)) {
          this.crud.formPending[index].quantity = this.crud.formPending[index].maxQuantity
          this.forceUpdate()
        }

        if( parseFloat(this.crud.formPending[index].quantity) <= 0) {
          this.crud.formPending[index].quantity = this.crud.formPending[index].maxQuantity
          this.crud.formPending[index].check = false
          this.forceUpdate()
        }
      },
      forceUpdate() {
        this.itemForceUpdate = this.itemForceUpdate + 1
      },
      saleValidDetailItems() {
        this.crud.form.detail = []

        if(this.crud.formPending.length) {
          this.crud.formPending.forEach(element => {
            if(element.check && parseFloat(element.quantity)>0) {

              if(this.parameters.remitFromOrder) {
                this.crud.form.detail.push({
                  'quantity': element.quantity,
                  'unit_measurement_id': element.unit_measurement_id,
                  'order_detail_id': element.order_detail_id,
                  'description': element.description,
                })
              }

              if(this.parameters.remitFromSale) {
                this.crud.form.detail.push({
                  'quantity': element.quantity,
                  'unit_measurement_id': element.unit_measurement_id,
                  'sale_detail_id': element.sale_detail_id,
                  'description': element.description,
                })
              }
            }
          });
        }
        if(this.crud.form.detail.length) {
          return true;
        } else {
          this.$awn.alert("No se cargó ningún item");
          return false;
        }
      },
      cancel() {
        this.confirmDialog('¿Desea cancelar el remito?', {
          title: 'Cancelar el Remito',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            this.modal.form.active = false

            // blanqueo remit from
            this.crud.form.orders_id = 0
            this.crud.form.orders = null
            this.wizard.startIndex = 0
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },

      // SELECT VOUCHER
      searchVoucherByCustomer() {
        if(!this.crud.form.id) {
          if(this.crud.form.customers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherByCustomer({
              'customers_id': this.crud.form.customers_id,
              'point_sale_id': this.crud.form.points_sales_id,
              'reference': this.crud.form.selectTypeVoucher,
            });
            result.then((response) => {
              this.crud.form.types_vouchers_id = response.data[0].id
              this.searchVoucher()
            })
            .catch(error => {
              this.$awn.alert(Error.showError(error));
            })
          }
        } else {
          if( this.crud.form.types_vouchers ) {
            this.modal.form.title = this.crud.form.types_vouchers.name
            this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
          }
        }
      },
      searchVoucher() {
        if(!this.crud.form.id) {
          if(this.crud.form.customers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherById({
              'points_sales_id': this.crud.form.points_sales_id,
              'types_vouchers_id' : this.crud.form.types_vouchers_id,
            });
            result.then((response) => {
              this.crud.form.types_vouchers = response.data.type_voucher
              this.crud.form.letter = response.data.type_voucher.letter
              this.crud.form.number = response.data.number

              if( this.crud.form.types_vouchers ) {
                this.modal.form.title = this.crud.form.types_vouchers.name
                this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
                this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
              }
            })
          }
        } else {
          if( this.crud.form.types_vouchers ) {
            this.modal.form.title = this.crud.form.types_vouchers.name
            this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
          }
        }
      },

      // FILTER
      filterLoad() {
        this.filterLoadCustomers()
        this.filterLoadPointSale()
        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      filterLoadCustomers() {
        var result = serviceAPI.obtenerClientes()
        result.then((response) => {
          var data = response.data

          this.arr.filters.customers = []
          data.forEach(element => {
            if(element.code) {
              this.arr.filters.customers.push({ code: element.id, label: element.code + ' | ' + element.name })
            } else {
              this.arr.filters.customers.push({ code: element.id, label: element.name })
            }
          });
        })
      },
      filterLoadPointSale() {
        var result = serviceAPI.obtenerPuntosVentas()
        result.then((response) => {
          var data = response.data

          this.arr.filters.points_sales = []
          data.forEach(element => {
            this.arr.filters.points_sales.push({ code: element.id, label: element.full_identification })
          });
        })
      },
      filterRemits(forceOpenSub=false) {
        this.table.isBusy = true
        var result = serviceAPI.filtrarRemitos(this.filters, this.table.currentPage)
        result.then((response) => {
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data
          this.arr.remits = data.data
          this.prepareExport(data.data)

          this.table.items.forEach(element => {
            if(forceOpenSub && this.itemSelected && element.id == this.itemSelected.id) {
              this.openSub(element)
            }
          });

          this.table.isBusy = false
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(ErrorToken.valid(error));
        })
      },

      // REMITS FROM ORDERS
      remitsFromOrders() {
        var result = serviceAPI.mostrarPedido(this.crud.form.orders_id);
        result.then((response) => {
          var data = response.data

          if(data.remitted) {
            this.$awn.alert("El pedido ya se encuentra remitido");
            return false
          }

          this.wizard.startIndex = 1
          this.add()

          setTimeout(()=>{
            this.crud.form.orders = data
            this.crud.form.customers_id = data.customers_id
            this.crud.form.customers = data.customer
            this.crud.form.points_sales_id = data.erp_points_sales_id
            this.crud.form.points_sales = data.points_sales

            this.saleValidDetailGeneral();
            setTimeout(()=>{
              this.crud.form.reference_voucher_id = data.id
            },1000)

          },1000)
        })
        .catch(error => {
          // blanqueo remit from
          this.crud.form.orders_id = 0
          this.crud.form.orders = null
          this.wizard.startIndex = 0

          this.$awn.alert(Error.showError(error));
        })
      },

      // COMPROBANTE DETAIL
      openSub(item) {
        this.itemSelected = item

        this.tableSub.items = item.details
        this.detailDrawer = true
      },
      hideSub() {
        this.tableSub.items=[]
        this.itemSelected = null
        this.detailDrawer = false
      },
      // Anterior/siguiente desde el drawer: reusa openSub() con el vecino,
      // así el contenido y el resaltado de fila se actualizan por el mismo
      // camino que un click directo en la tabla.
      goPrevRemit() {
        if(!this.canGoPrevRemit) return
        var item = this.table.items[this.currentRemitIndex - 1]
        if(item) this.openSub(item)
      },
      goNextRemit() {
        if(!this.canGoNextRemit) return
        var item = this.table.items[this.currentRemitIndex + 1]
        if(item) this.openSub(item)
      },
      openDetail() {
        this.tableDetail.items = this.itemSelected.details

        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = "Detalle del Remito"
        this.modal.detail.active = true
      },
      getProductCode(item) {
        if(item.orders_detail) {
          item = item.orders_detail
        }

        if(item.sales_detail) {
          item = item.sales_detail
        }

        var code = ''
        var prod = null

        if(item.code) {
          code = item.code
        }

        if(item.products_id) {
          if(code) {
            return code
          } else {
            return item.product.code
          }
        }

        if(item.products_colors_id) {
          prod = item.products_color
          if(code) {
            return code
          } else {
            return prod.code
          }
        }

        if(item.products_waist_id) {
          prod = item.products_waist
          if(code) {
            return code
          } else {
            return prod.code
          }
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist
          if(code) {
            return code
          } else {
            return prod.code
          }
        }

        if(item.products_compound_id) {
          prod = item.products_compound
          if(code) {
            return code
          } else {
            return prod.code
          }
        }
      },
      getProductName(item) {
        if(item.orders_detail) {
          item = item.orders_detail
        }

        if(item.sales_detail) {
          item = item.sales_detail
        }

        var name = ''
        var prod = null

        if(item.description) {
          name = item.description
        }

        if(item.products_id) {
          if(name) {
            return name
          } else {
            return item.product.name
          }
        }

        if(item.products_colors_id) {
          prod = item.products_color
          if(name) {
            return name + ' (' + prod.color.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ')'
          }
        }

        if(item.products_waist_id) {
          prod = item.products_waist
          if(name) {
            return name + ' (' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.waist.name + ')'
          }
        }

        if(item.products_color_waist_id) {
          prod = item.products_color_waist
          if(name) {
            return name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          } else {
            return prod.name + ' (' + prod.color.name + ' - ' + prod.waist.name + ')'
          }
        }

        if(item.products_compound_id) {
          prod = item.products_compound
          if(name) {
            return name
          } else {
            return prod.name
          }
        }
      },
      // Versión sin HTML de getProductName(), para usar en atributos title/
      // tooltip (evita mostrar tags crudos en el tooltip nativo).
      getProductNamePlain(item) {
        return String(this.getProductName(item) || '').replace(/<[^>]*>/g, '')
      },

      controlsStock(item) {
        if(item && item.control_stock !== undefined && item.control_stock !== null) {
          return !!item.control_stock
        }
        const product = item && item.product ? item.product : null
        if(product && product.control_stock !== undefined && product.control_stock !== null) {
          return !!product.control_stock
        }
        return true
      },
      stockBadgeLabel(item) {
        if(item && item.has_deposit_movement) return 'Movimiento registrado'
        return this.controlsStock(item) ? 'Controla stock · sin movimiento' : 'No controla stock'
      },
      stockBadgeVariant(item) {
        if(item && item.has_deposit_movement) return 'success'
        return this.controlsStock(item) ? 'warning' : 'secondary'
      },
      stockBadgeTitle(item) {
        return item && item.has_deposit_movement
          ? 'Este ítem tiene un movimiento de depósito asociado al remito'
          : (this.controlsStock(item) ? 'El producto controla stock, pero este remito no tiene movimiento asociado' : 'El producto no controla stock')
      },

      // PRINT
      sendPrinter(item) {
        this.crud.print.key=Math.random()
        this.crud.print.id=item.id
        this.crud.print.reference = item.reportDefault
      },

      // INVOICE
      generateInvoice(item) {
        this.$router.push({ name: 'SalesStaffSalesGenerateByRemits', params: {remitsID: item.id} })
      }
    }
  }
</script>
<style>
  @media (min-width: 1200px) {
    .remit-detail-modal-wide { max-width: min(96vw, 1560px); }
  }
  .remit-detail-id { color: var(--app-color-text-secondary); font-size: 12px; padding: 4px 8px; }
  .remit-detail-business,
  .remit-detail-point-sale { padding: 5px 8px; font-weight: var(--app-font-semibold); }
  .remit-detail-voucher { display: inline-flex; align-items: center; }
  .remit-detail-staff { display: inline-flex; align-items: center; gap: 8px; font-weight: var(--app-font-semibold); }
  .crud-remits-wizard .wizard-header {
    padding: 0px;
  }
  .crud-remits-wizard .wizard-card-footer {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 4px 0;
  }
  .crud-remits-wizard .wizard-card-footer .wizard-footer-left,
  .crud-remits-wizard .wizard-card-footer .wizard-footer-right { float: none !important; }
  .crud-remits-wizard .wizard-card-footer .wizard-footer-right { margin-left: auto; }
  .crud-remits-wizard .wizard-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 112px;
    height: 36px;
    padding: 6px 14px;
    border-width: 1px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: var(--app-font-semibold);
    box-shadow: none;
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .crud-remits-wizard .wizard-footer-left .wizard-btn::before,
  .crud-remits-wizard .wizard-footer-right .wizard-btn::after {
    font-family: 'Material Symbols Rounded';
    font-size: 18px;
    font-weight: normal;
    font-style: normal;
    font-feature-settings: 'liga';
    line-height: 1;
  }
  .crud-remits-wizard .wizard-footer-left .wizard-btn::before { content: 'arrow_back'; margin-right: 6px; }
  .crud-remits-wizard .wizard-footer-right .wizard-btn::after { content: 'arrow_forward'; margin-left: 6px; }
  .crud-remits-wizard .wizard-footer-left .wizard-btn {
    background-color: var(--app-color-surface) !important;
    border-color: var(--app-color-border-strong) !important;
    color: var(--app-color-text-secondary) !important;
  }
  .crud-remits-wizard .wizard-footer-left .wizard-btn:hover {
    background-color: var(--app-color-surface-subtle) !important;
    color: var(--app-color-text) !important;
  }
  .crud-remits-wizard .wizard-footer-right .wizard-btn {
    background-color: var(--app-color-primary) !important;
    border-color: var(--app-color-primary) !important;
    color: #fff !important;
  }
  .crud-remits-wizard .wizard-footer-right .wizard-btn:hover { filter: brightness(.94); }
  @media (max-width: 767.98px) {
    .crud-remits-wizard .wizard-card-footer { gap: 8px; padding: 12px 0 0; }
    .crud-remits-wizard .wizard-footer-left,
    .crud-remits-wizard .wizard-footer-right { display: flex; flex: 1 1 0; }
    .crud-remits-wizard .wizard-card-footer .wizard-footer-right { margin-left: 0; }
    .crud-remits-wizard .wizard-footer-left > span,
    .crud-remits-wizard .wizard-footer-right > span { display: block; width: 100%; }
    .crud-remits-wizard .wizard-btn { width: 100%; min-width: 0; }
  }
  .crud-remit-item-input {
    height: 25px;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
  }
  .crud-remit-table-items {
    max-height: 350px;
    overflow: auto;
  }
</style>
<style scoped>
  .b-avatar {
    width: 25px;
    height: 25px;
  }
</style>
